import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

/**
 * Product info tabs on desktop, accordion panels on mobile — same markup for both.
 */
class ProductInfoTabs extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#tablist = this.querySelector('[data-product-info-tablist]');
    this.#accordions = Array.from(this.querySelectorAll('[data-product-info-accordion]'));

    this.#ensureTablist();
    this.#tabs = Array.from(this.querySelectorAll('[data-product-info-tab]'));
    this.#syncTabsToAccordions();

    this.#tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.#selectTab(tab.dataset.tabId), { signal });
    });

    mediaQueryLarge.addEventListener('change', () => this.#updateLayout(), { signal });
    this.#updateLayout();
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /** @type {HTMLElement | null} */
  #tablist = null;

  /** @type {HTMLElement[]} */
  #tabs = [];

  /** @type {HTMLElement[]} */
  #accordions = [];

  #syncTabsToAccordions() {
    const accordionIds = new Set(this.#accordions.map((item) => item.dataset.tabId));

    this.#tabs.forEach((tab) => {
      const missing = !accordionIds.has(tab.dataset.tabId);
      tab.hidden = missing;
      if (missing) {
        tab.setAttribute('aria-selected', 'false');
      }
    });

    this.#tabs = this.#tabs.filter((tab) => !tab.hidden);
  }

  #getTabHeading(accordion) {
    if (!(accordion instanceof HTMLElement)) return '';

    return (
      accordion.dataset.tabHeading ||
      accordion.querySelector('.product-info-tabs__summary span')?.textContent?.trim() ||
      ''
    );
  }

  #ensureTablist() {
    if (!this.#tablist || this.#accordions.length === 0) return;

    const existingTabs = Array.from(this.querySelectorAll('[data-product-info-tab]'));
    const needsRebuild =
      existingTabs.length === 0 ||
      existingTabs.every((tab) => !tab.textContent?.trim());

    if (!needsRebuild) {
      existingTabs.forEach((tab) => {
        const tabId = tab.dataset.tabId;
        if (!tabId || tab.textContent?.trim()) return;

        const accordion = this.#accordions.find((item) => item.dataset.tabId === tabId);
        const heading = this.#getTabHeading(accordion);

        if (heading) {
          tab.textContent = heading;
        }
      });
      return;
    }

    this.#tablist.innerHTML = '';

    this.#accordions.forEach((accordion, index) => {
      const tabId = accordion.dataset.tabId;
      if (!tabId) return;

      const heading = this.#getTabHeading(accordion);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'product-info-tabs__tab';
      button.dataset.productInfoTab = '';
      button.dataset.tabId = tabId;
      button.id = `product-info-tab-${tabId}-${this.dataset.sectionId}`;
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
      button.setAttribute('aria-controls', `product-info-panel-${tabId}-${this.dataset.sectionId}`);
      button.textContent = heading;
      this.#tablist.appendChild(button);
    });
  }

  /**
   * @param {string} tabId
   */
  #selectTab(tabId) {
    if (!tabId) return;

    let matched = false;

    this.#tabs.forEach((tab) => {
      const isSelected = tab.dataset.tabId === tabId;
      tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    });

    this.#accordions.forEach((accordion) => {
      const isActive = accordion.dataset.tabId === tabId;
      accordion.classList.toggle('is-active', isActive);

      if (isActive) {
        matched = true;
      }

      const details = accordion.querySelector('details');
      if (details instanceof HTMLDetailsElement) {
        if (!isMobileBreakpoint() && isActive) {
          details.open = true;
        }
      }
    });

    if (!matched && this.#accordions[0]) {
      this.#activateAccordion(this.#accordions[0]);
    }
  }

  /** @param {HTMLElement} accordion */
  #activateAccordion(accordion) {
    const tabId = accordion.dataset.tabId;
    if (!tabId) return;

    this.#accordions.forEach((item) => {
      const isActive = item === accordion;
      item.classList.toggle('is-active', isActive);

      const details = item.querySelector('details');
      if (details instanceof HTMLDetailsElement && !isMobileBreakpoint() && isActive) {
        details.open = true;
      }
    });

    this.#tabs.forEach((tab) => {
      tab.setAttribute('aria-selected', tab.dataset.tabId === tabId ? 'true' : 'false');
    });
  }

  #updateLayout() {
    const isMobile = isMobileBreakpoint();

    this.classList.toggle('product-info-tabs--mobile', isMobile);
    this.classList.toggle('product-info-tabs--desktop', !isMobile);

    if (this.#tablist) {
      this.#tablist.hidden = isMobile;
    }

    if (isMobile) {
      this.#accordions.forEach((accordion) => {
        accordion.classList.add('is-active');
        accordion.hidden = false;
      });
      return;
    }

    const selectedTab =
      this.#tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') ?? this.#tabs[0];

    if (selectedTab?.dataset.tabId) {
      this.#selectTab(selectedTab.dataset.tabId);
      return;
    }

    const defaultAccordion =
      this.#accordions.find((accordion) => accordion.classList.contains('is-active')) ??
      this.#accordions[0];

    if (defaultAccordion) {
      this.#activateAccordion(defaultAccordion);
    }
  }
}

if (!customElements.get('product-info-tabs')) {
  customElements.define('product-info-tabs', ProductInfoTabs);
}
